import React, { useState } from 'react';
import { Modal, Table, Tag, Button, DatePicker, Input, Popconfirm, message, Alert, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, UndoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { addRoutePause, deleteRoutePause, editRoutePause, revertRoutePauseEdit } from '../modules/routes/api';

const fmt = (v) => (v ? dayjs(v).format('DD MMM HH:mm') : '—');
const durMins = (p) => {
  if (!p.start) return null;
  const end = p.end ? dayjs(p.end) : dayjs();
  const mins = Math.max(0, Math.round(end.diff(dayjs(p.start), 'second') / 60));
  return `${mins}′${p.end ? '' : ' (ongoing)'}`;
};

/**
 * P1 Pause/Resume — operator view of a route's pauses. Lists driver-badged
 * and operator-added pauses; lets the operator ADD one the driver forgot
 * (backdated, audited), DELETE operator-added ones, and (#133) CORRECT the
 * start / end of any pause — the driver's original times are kept and the
 * correction can be reverted.
 */
export default function RoutePausesModal({ route, open, onClose, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [note, setNote] = useState('');
  // #133 — the pause being corrected (the form below switches to edit mode).
  const [editing, setEditing] = useState(null);

  const pauses = route?.pauses || [];
  const routeDay = route?.date ? dayjs(route.date) : dayjs();

  const submit = async () => {
    if (!start || !end) {
      message.warning('Pick both start and end of the pause');
      return;
    }
    if (!end.isAfter(start)) {
      message.warning('Pause end must be after its start');
      return;
    }
    setSaving(true);
    const payload = {
      start: start.toISOString(),
      end: end.toISOString(),
      note: note?.trim() || undefined,
    };
    const { response, error } = editing
      ? await editRoutePause(route.id, editing.pauseLogId, payload)
      : await addRoutePause(route.id, payload);
    setSaving(false);
    if (error) {
      message.error(error.message || (editing ? 'Failed to correct the pause' : 'Failed to add the pause'));
      return;
    }
    message.success(editing ? 'Pause corrected — durations updated' : 'Pause added — durations updated');
    resetForm();
    onUpdated?.(response);
  };

  const resetForm = () => {
    setEditing(null);
    setStart(null);
    setEnd(null);
    setNote('');
  };

  const startEdit = (p) => {
    setEditing(p);
    setStart(p.start ? dayjs(p.start) : null);
    setEnd(p.end ? dayjs(p.end) : null);
    setNote('');
  };

  const revert = async (p) => {
    const { response, error } = await revertRoutePauseEdit(route.id, p.pauseLogId);
    if (error) {
      message.error(error.message || 'Failed to revert the correction');
      return;
    }
    message.success("Driver's original times restored");
    if (editing?.pauseLogId === p.pauseLogId) resetForm();
    onUpdated?.(response);
  };

  const remove = async (p) => {
    const { response, error } = await deleteRoutePause(route.id, p.pauseLogId);
    if (error) {
      message.error(error.message || 'Failed to delete the pause');
      return;
    }
    message.success('Pause deleted');
    onUpdated?.(response);
  };

  const columns = [
    { title: 'Start', key: 's', render: (_, p) => fmt(p.start) },
    { title: 'End', key: 'e', render: (_, p) => fmt(p.end) },
    { title: 'Duration', key: 'd', render: (_, p) => durMins(p) },
    {
      title: 'Source',
      key: 'src',
      render: (_, p) => (
        <>
          {p.simulatedByOperator ? <Tag color="orange">Operator</Tag> : <Tag color="green">Driver</Tag>}
          {p.edited && (
            <Tooltip
              title={`Corrected ${fmt(p.edited.correctedAt)} — original: ${fmt(p.edited.originalStart)} → ${fmt(
                p.edited.originalEnd,
              )}`}
            >
              <Tag color="purple" data-testid="pause-edited-tag">
                Edited
              </Tag>
            </Tooltip>
          )}
        </>
      ),
    },
    { title: 'Note', dataIndex: 'note', key: 'n', render: (v) => v || '—' },
    {
      // Where the driver badged the pause (fresh GPS fix from the app).
      title: '',
      key: 'geo',
      width: 40,
      render: (_, p) =>
        p.lat != null && p.lng != null ? (
          <Tooltip title={`${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`}>
            <a href={`https://www.google.com/maps?q=${p.lat},${p.lng}`} target="_blank" rel="noreferrer">
              📍
            </a>
          </Tooltip>
        ) : null,
    },
    {
      title: '',
      key: 'a',
      width: 108,
      render: (_, p) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <Tooltip title="Correct start / end">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => startEdit(p)}
              aria-label="Edit pause"
            />
          </Tooltip>
          {p.edited && (
            <Popconfirm title="Restore the driver's original times?" onConfirm={() => revert(p)}>
              <Tooltip title="Revert the correction">
                <Button type="text" size="small" icon={<UndoOutlined />} aria-label="Revert pause correction" />
              </Tooltip>
            </Popconfirm>
          )}
          {p.simulatedByOperator && (
            <Popconfirm title="Delete this operator-added pause?" onConfirm={() => remove(p)}>
              <Button danger type="text" size="small" icon={<DeleteOutlined />} aria-label="Delete pause" />
            </Popconfirm>
          )}
        </span>
      ),
    },
  ];

  return (
    <Modal title="⏸ Route pauses" open={open} onCancel={onClose} footer={null} width={720}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Paused time is excluded from worked hours (shift) and from billable client service."
        description="Add a pause the driver forgot to badge, or correct a wrong one (✎): the driver's original times are kept and the correction can be reverted."
      />
      <Table
        size="small"
        rowKey={(p) => p.pauseLogId}
        columns={columns}
        dataSource={pauses}
        pagination={false}
        locale={{ emptyText: 'No pauses on this route' }}
        style={{ marginBottom: 16 }}
      />
      {editing && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message={`Correcting the pause of ${fmt(editing.start)} → ${fmt(editing.end)}`}
        />
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <DatePicker
          showTime={{ format: 'HH:mm' }}
          format="DD MMM HH:mm"
          placeholder="Pause start"
          value={start}
          onChange={setStart}
          defaultPickerValue={routeDay}
        />
        <DatePicker
          showTime={{ format: 'HH:mm' }}
          format="DD MMM HH:mm"
          placeholder="Pause end"
          value={end}
          onChange={setEnd}
          defaultPickerValue={routeDay}
        />
        <Input
          placeholder="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={{ width: 180 }}
        />
        <Button type="primary" icon={editing ? <EditOutlined /> : <PlusOutlined />} loading={saving} onClick={submit}>
          {editing ? 'Save correction' : 'Add pause'}
        </Button>
        {editing && <Button onClick={resetForm}>Cancel</Button>}
      </div>
    </Modal>
  );
}
